import React, { useState } from 'react';
import FacilityResultItem from './FacilityResultItem';
import { SearchResultSkeleton, EmptySearchState, SearchErrorState, LoadingSpinner } from './LoadingStates';

const BLMMapSidebar = ({
  isOpen,
  isMobile,
  selectedTable,
  searchTerm,
  searchResults,
  searchLoading,
  searchError,
  searchCount,
  currentPage,
  resultsPerPage,
  onPageChange,
  selectedFacility,
  fieldNotes,
  location,
  selectedFieldNotesId,
  onFieldNotesSelect,
  onFacilityLocate,
  onFacilityExpand,
  expandedIds,
  selectedState,
  onStateChange,
  filteredStates,
  activityTags,
  onActivityTagToggle
}) => {
  const [locationFilterOpen, setLocationFilterOpen] = useState(false);
  const [activityFilterOpen, setActivityFilterOpen] = useState(false);

  if (!isOpen) return null;

  const totalPages = Math.ceil(searchCount / resultsPerPage);

  return (
    <div style={{
      width: isMobile ? '100vw' : 400,
      height: '100%',
      background: "white",
      borderRight: isMobile ? 'none' : "1px solid #e2e8f0",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      boxShadow: '2px 0 8px rgba(0,0,0,0.12)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      
      {/* Filter Section */}
      <div style={{ padding: 16 }}>
        {/* Location Filter */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#334155" }}>Location</div>
            <button
              onClick={() => setLocationFilterOpen(open => !open)}
              style={{ 
                background: '#e0f2fe', 
                color: '#0891b2', 
                border: 'none', 
                borderRadius: 8, 
                padding: '2px 10px', 
                fontWeight: 600, 
                fontSize: 13, 
                cursor: 'pointer' 
              }}
              aria-expanded={locationFilterOpen}
              aria-controls="location-filter"
            >
              {locationFilterOpen ? 'Hide' : 'Show'}
            </button>
          </div>
          {locationFilterOpen && (
            <div id="location-filter" style={{ margin: '12px 0' }}>
              <label htmlFor="state-select">Choose a state:</label>
              <select
                id="state-select"
                value={selectedState}
                onChange={e => onStateChange(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 6, marginBottom: 6 }}
              >
                <option>All States</option>
                {filteredStates.map(s => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Activity Type Filter */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#334155" }}>Activity Type</div>
            <button
              onClick={() => setActivityFilterOpen(open => !open)}
              style={{ 
                background: '#e0f2fe', 
                color: '#0891b2', 
                border: 'none', 
                borderRadius: 8, 
                padding: '2px 10px', 
                fontWeight: 600, 
                fontSize: 13, 
                cursor: 'pointer' 
              }}
              aria-expanded={activityFilterOpen}
              aria-controls="activity-type-filter"
            >
              {activityFilterOpen ? 'Hide' : 'Show'}
            </button>
          </div>
          {activityFilterOpen && (
            <div id="activity-type-filter" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {activityTags.map((tag, idx) => (
                <span
                  key={tag.label}
                  onClick={() => onActivityTagToggle(idx)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 4,
                    background: tag.active ? "#3b82f6" : "#f1f5f9",
                    color: tag.active ? "white" : "#475569",
                    fontSize: 12,
                    cursor: "pointer"
                  }}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Current Selection */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 8 }}>Current Selection</div>
          {selectedFacility ? (
            <FacilityResultItem
              id={selectedFacility.id || selectedFacility.name}
              name={selectedFacility.name}
              description={selectedFacility.description}
              state={selectedFacility.state}
              latitude={selectedFacility.latitude || selectedFacility.RecAreaLatitude}
              longitude={selectedFacility.longitude || selectedFacility.RecAreaLongitude}
              facility_subtype={selectedFacility.facility_subtype || selectedFacility.subtype}
              unit_name={selectedFacility.unit_name}
              photo_thumb={selectedFacility.photo_thumb}
              expanded={selectedFacility.expanded}
              selectedFieldNotesId={selectedFieldNotesId}
              onFieldNotes={onFieldNotesSelect}
              onLocate={onFacilityLocate}
              // Vendor-specific props
              vendor_name={selectedFacility.vendor_name}
              category={selectedFacility.category}
              rating={selectedFacility.rating}
              website={selectedFacility.website}
              formatted_address={selectedFacility.formatted_address}
              international_phone_number={selectedFacility.international_phone_number}
              content={selectedFacility.content}
            />
          ) : fieldNotes && fieldNotes.name ? (
            <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{fieldNotes.name}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Lat: {fieldNotes.latitude}, Lon: {fieldNotes.longitude}</div>
              {fieldNotes.subtype && <div style={{ fontSize: 13, color: '#64748b' }}>{fieldNotes.subtype}</div>}
              {fieldNotes.unit_name && <div style={{ fontSize: 13, color: '#64748b' }}>{fieldNotes.unit_name}</div>}
            </div>
          ) : location && location.name ? (
            <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{location.name}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Lat: {location.lat}, Lon: {location.lon}</div>
            </div>
          ) : (
            <div style={{ color: '#64748b', fontSize: 13 }}>No selection</div>
          )}
        </div>
      </div>

      {/* Search Results Section */}
      <div style={{ padding: 20, background: "#f8fafc", flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, color: "#334155" }}>
          BLM Recreation Site Points Search
        </div>
        
        {selectedTable === 'blm_facilities' ? (
          <>
            {searchTerm && !searchLoading && !searchError && (
              <div style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>
                {searchCount} result{searchCount === 1 ? '' : 's'} found
                {searchCount > resultsPerPage && (
                  <span style={{ marginLeft: 12, fontSize: 13, color: '#059669' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
            )}
            
            {searchLoading && <SearchResultSkeleton count={5} />}
            {searchError && <SearchErrorState error={searchError} />}
            {!searchLoading && !searchError && searchResults.length === 0 && searchTerm && (
              <EmptySearchState searchTerm={searchTerm} />
            )}
            {!searchLoading && !searchError && searchResults.length === 0 && !searchTerm && (
              <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: 32, marginBottom: 16 }}>🏕️</div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Enter a search term</div>
                <div style={{ fontSize: 14 }}>Search for BLM recreation facilities to get started</div>
              </div>
            )}
            
            {/* Results List */}
            <div style={{ flex: 1, overflowY: "auto", borderBottom: "1px solid #e2e8f0" }}>
              {searchResults.map((item, idx) => {
                const expanded = expandedIds.includes(item.id);
                const isSelected = location && location.name === item.name && location.lat === item.latitude && location.lon === item.longitude;
                const ref = isSelected ? (el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }) : undefined;
                
                return (
                  <div
                    ref={ref}
                    key={item.id || idx}
                    style={{
                      boxShadow: isSelected ? '0 0 0 3px #3b82f6' : undefined,
                      borderRadius: 8,
                      borderTop: '1px solid #e2e8f0',
                      borderRight: '1px solid #e2e8f0',
                      borderBottom: '1px solid #e2e8f0',
                      borderLeft: expanded ? '4px solid #059669' : '1px solid #e2e8f0',
                    }}
                  >
                    <FacilityResultItem
                      id={item.id}
                      name={item.name}
                      description={item.description}
                      state={item.state}
                      expanded={expanded}
                      selectedFieldNotesId={selectedFieldNotesId}
                      onFieldNotes={onFieldNotesSelect}
                      onLocate={() => onFacilityLocate(item)}
                      onExpand={() => onFacilityExpand(item.id)}
                      latitude={item.latitude}
                      longitude={item.longitude}
                      facility_subtype={item.facility_subtype}
                      unit_name={item.unit_name}
                      photo_thumb={item.photo_thumb}
                      photo_link={item.photo_link}
                      photo_text={item.photo_text}
                      url={item.url}
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : selectedTable === 'rec_areas' ? (
          <>
            {searchTerm && !searchLoading && !searchError && (
              <div style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>
                {searchCount} result{searchCount === 1 ? '' : 's'} found
                {searchCount > resultsPerPage && (
                  <span style={{ marginLeft: 12, fontSize: 13, color: '#059669' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
            )}
            
            {searchLoading && <SearchResultSkeleton count={5} />}
            {searchError && <SearchErrorState error={searchError} />}
            {!searchLoading && !searchError && searchResults.length === 0 && searchTerm && (
              <EmptySearchState searchTerm={searchTerm} />
            )}
            {!searchLoading && !searchError && searchResults.length === 0 && !searchTerm && (
              <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: 32, marginBottom: 16 }}>🏔️</div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Enter a search term</div>
                <div style={{ fontSize: 14 }}>Search for BLM recreation areas to get started</div>
              </div>
            )}
            
            {/* Results List */}
            <div style={{ flex: 1, overflowY: "auto", borderBottom: "1px solid #e2e8f0" }}>
              {searchResults.map((item, idx) => {
                const expanded = expandedIds.includes(item.id);
                const isSelected = location && location.name === item.name && location.lat === item.latitude && location.lon === item.longitude;
                const ref = isSelected ? (el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }) : undefined;
                
                return (
                  <div
                    ref={ref}
                    key={item.id || idx}
                    style={{
                      boxShadow: isSelected ? '0 0 0 3px #3b82f6' : undefined,
                      borderRadius: 8,
                      borderTop: '1px solid #e2e8f0',
                      borderRight: '1px solid #e2e8f0',
                      borderBottom: '1px solid #e2e8f0',
                      borderLeft: expanded ? '4px solid #059669' : '1px solid #e2e8f0',
                    }}
                  >
                    <FacilityResultItem
                      id={item.id}
                      name={item.name}
                      description={item.description}
                      state={item.state}
                      expanded={expanded}
                      selectedFieldNotesId={selectedFieldNotesId}
                      onFieldNotes={onFieldNotesSelect}
                      onLocate={() => onFacilityLocate(item)}
                      onExpand={() => onFacilityExpand(item.id)}
                      latitude={item.latitude}
                      longitude={item.longitude}
                      facility_subtype={item.facility_subtype}
                      unit_name={item.unit_name}
                      photo_thumb={item.photo_thumb}
                      photo_link={item.photo_link}
                      photo_text={item.photo_text}
                      url={item.url}
                      RecAreaDirections={item.RecAreaDirections}
                      RecAreaReservationURL={item.RecAreaReservationURL}
                      RecAreaUseFeeDescription={item.RecAreaUseFeeDescription}
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div>Select a table to search</div>
        )}

        {/* Pagination */}
        {searchCount > resultsPerPage && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 16 }}>
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{ 
                padding: '6px 16px', 
                borderRadius: 6, 
                border: '1px solid #d1d5db', 
                background: currentPage === 1 ? '#f1f5f9' : 'white', 
                color: '#334155', 
                fontWeight: 600, 
                fontSize: 13, 
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer' 
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: 13, color: '#059669' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{ 
                padding: '6px 16px', 
                borderRadius: 6, 
                border: '1px solid #d1d5db', 
                background: currentPage === totalPages ? '#f1f5f9' : 'white', 
                color: '#334155', 
                fontWeight: 600, 
                fontSize: 13, 
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' 
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BLMMapSidebar;